import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, EmptyState, Field } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import VideoCall from "@/components/VideoCall";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CalendarClock, Video, Clock, Gift } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const minsUntil = (iso) => (iso ? Math.round((new Date(iso) - new Date()) / 60000) : null);

/** Coordinator/admin card: student meeting requests to confirm and calls to start. */
export function TaskMeetings({ meetings, onChanged }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [proposeFor, setProposeFor] = useState(null);
  const [when, setWhen] = useState("");
  const [call, setCall] = useState(null);
  const rows = (meetings || []).filter((m) => m.status !== "Completed" || (m.gift_card_choice && !m.gift_card_sent));
  const giftsToSend = rows.filter((m) => m.gift_card_choice && !m.gift_card_sent).length;

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openPropose = (m) => {
    setProposeFor(m);
    const d = new Date(m.scheduled_start || Date.now() + 86400000);
    setWhen(new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16));
  };

  const sendPropose = () => {
    if (!when) return toast.error("Pick a date and time");
    const m = proposeFor;
    setProposeFor(null);
    act(() => api.post(`/task-meetings/${m.id}/propose`, { scheduled_start: new Date(when).toISOString() }),
      "New time sent — the student confirms it on their dashboard");
  };

  const startMeeting = async (m) => {
    setBusy(true);
    try {
      await api.post(`/task-meetings/${m.id}/start`);
      const { data: tok } = await api.post(`/video-meetings/${m.id}/token`);
      setCall({ id: m.id, roomUrl: tok.room_url, token: tok.token, isOwner: tok.is_owner });
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const closeCall = async () => {
    if (call) { try { await api.post(`/video-meetings/${call.id}/end`); } catch { /* already closed */ } }
    setCall(null); onChanged?.();
  };

  return (
    <Card title="Student meetings" data-testid="task-meetings-card">
      {rows.length === 0 ? <EmptyState text="No student meeting requests right now." /> : (
        <div className="space-y-3">
          {rows.map((m) => {
            const openIn = m.can_start_at ? minsUntil(m.can_start_at) : null;
            return (
              <div key={m.id} data-testid={`task-meeting-${m.id}`}
                className={`border rounded-md p-4 ${m.awaiting_me ? "border-cc-orange bg-orange-50/40" : m.status === "Started" ? "cc-flash border-cc-orange" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-900">{m.student_name}</p>
                    <p className="text-xs text-zinc-500">{m.task_title}</p>
                    <p className="text-xs text-zinc-600 mt-1 flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5 text-cc-blue" /><b>{fmt(m.scheduled_start)}</b></p>
                    {m.gift_card_choice && (
                      <p className={`text-xs mt-1 flex items-center gap-1.5 ${m.gift_card_sent ? "text-zinc-500" : "text-orange-700"}`} data-testid={`cc-gift-${m.id}`}>
                        <Gift className="w-3.5 h-3.5 text-cc-orange" />Gift card: <b>{m.gift_card_choice}</b>
                        {m.gift_card_sent ? " · sent" : " · needs sending"}
                      </p>
                    )}
                    {m.status === "Pending Confirmation" && (m.awaiting_me
                      ? <p className="text-xs text-orange-700 mt-1">{m.proposed_by_name} asked for this time — confirm it or propose another.</p>
                      : <p className="text-xs text-amber-700 mt-1">Waiting for {m.student_name} to confirm your new time.</p>)}
                    {m.status === "Scheduled" && openIn > 0 && (
                      <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />
                        Start Meeting opens in {openIn >= 60 ? `${Math.floor(openIn / 60)}h ${openIn % 60}m` : `${openIn}m`}</p>
                    )}
                  </div>
                  <StatusBadge status={m.status} />
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {m.awaiting_me && m.status === "Pending Confirmation" && (
                    <Button size="sm" disabled={busy} className="bg-cc-green text-white h-7 text-xs"
                      onClick={() => act(() => api.post(`/task-meetings/${m.id}/confirm`), "Confirmed — the student has been notified")}
                      data-testid={`cc-confirm-${m.id}`}>Confirm time</Button>
                  )}
                  {["Pending Confirmation", "Scheduled"].includes(m.status) && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openPropose(m)} data-testid={`cc-propose-${m.id}`}>Change date/time</Button>
                  )}
                  {["Scheduled", "Started"].includes(m.status) && (
                    <Button size="sm" disabled={busy || !m.can_start_now} onClick={() => startMeeting(m)}
                      className={m.can_start_now ? "bg-cc-orange text-white h-7 text-xs" : "h-7 text-xs bg-zinc-200 text-zinc-500 cursor-not-allowed"}
                      title={m.can_start_now ? "" : `Opens ${m.start_window_minutes || 15} minutes before the meeting`}
                      data-testid={`cc-start-${m.id}`}><Video className="w-3 h-3 mr-1" />{m.status === "Started" ? "Join" : "Start Meeting"}</Button>
                  )}
                  {m.gift_card_choice && !m.gift_card_sent && (
                    <Button size="sm" disabled={busy} className="bg-cc-orange text-white h-7 text-xs"
                      onClick={() => act(() => api.post(`/task-meetings/${m.id}/gift-card-sent`), `${m.gift_card_choice} gift card marked sent`)}
                      data-testid={`cc-gift-sent-${m.id}`}><Gift className="w-3 h-3 mr-1" />Mark {m.gift_card_choice} gift card sent</Button>
                  )}
                  {["Pending Confirmation", "Scheduled"].includes(m.status) && (
                    <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-200"
                      onClick={() => act(() => api.post(`/task-meetings/${m.id}/cancel`), "Meeting cancelled")}
                      data-testid={`cc-cancel-${m.id}`}>Cancel</Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!proposeFor} onOpenChange={(o) => !o && setProposeFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Change the date & time</DialogTitle></DialogHeader>
          <Field label="New date & time">
            <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="cc-propose-when" />
          </Field>
          <p className="text-xs text-zinc-400">{proposeFor?.student_name} will be asked to confirm this on their dashboard.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProposeFor(null)}>Cancel</Button>
            <Button onClick={sendPropose} disabled={busy} className="bg-cc-blue text-white" data-testid="cc-send-propose">Send for confirmation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {call && <VideoCall roomUrl={call.roomUrl} token={call.token} userName={user.name} isOwner={call.isOwner} onClose={closeCall} />}
    </Card>
  );
}
